/* ==========================================================================
   Rook Components — Shop The Look Controller
   Web Component: <rk-shop-the-look>
   Sincronização bidirecional entre hotspots e carrossel de produtos.
   ========================================================================== */

if (!customElements.get('rk-shop-the-look')) {
  class RkShopTheLook extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this._onHotspotClick = this._onHotspotClick.bind(this);
      this._onCarouselInit = this._onCarouselInit.bind(this);
      this._onProductLinkTap = this._onProductLinkTap.bind(this);
      this._onCartAddFromStl = this._onCartAddFromStl.bind(this);
      this._stlQuickAddPending = false;
    }

    connectedCallback() {
      this.hotspots = this.querySelectorAll('.rk-stl__hotspot');

      // Listen for hotspot clicks (event delegation)
      this.addEventListener('click', this._onHotspotClick);

      // Mobile/touch: card inteiro abre o quick-add em vez de navegar
      // (capture: precisa vencer a navegação do <a> esticado)
      this.addEventListener('click', this._onProductLinkTap, true);

      // Adição vinda do STL no mobile: não abre a sacola nem perde o scroll
      // (capture no document: roda antes do auto-open do cart-drawer)
      document.addEventListener('cart:update', this._onCartAddFromStl, true);

      // Listen for carousel init to capture Swiper instance
      this.addEventListener('rk:carousel:init', this._onCarouselInit);

      // Sync hotspot positions from slide data-attributes
      this._syncHotspotPositions();

      // Observe slide data-attribute changes (Shopify editor re-renders blocks)
      this._observeSlides();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onHotspotClick);
      this.removeEventListener('click', this._onProductLinkTap, true);
      document.removeEventListener('cart:update', this._onCartAddFromStl, true);
      this.removeEventListener('rk:carousel:init', this._onCarouselInit);

      if (this.swiper) {
        this.swiper.off('slideChange', this._onSlideChange);
        this.swiper = null;
      }

      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    /* ------------------------------------------------------------------
       Observe slides for data-attribute changes (editor re-renders)
       ------------------------------------------------------------------ */
    _observeSlides() {
      const carousel = this.querySelector('.rk-stl__carousel');
      if (!carousel) return;

      this._observer = new MutationObserver(() => {
        this._syncHotspotPositions();
      });

      this._observer.observe(carousel, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-hotspot-x', 'data-hotspot-y']
      });
    }

    /* ------------------------------------------------------------------
       Read positions from slide data-attributes → apply to hotspots
       ------------------------------------------------------------------ */
    _syncHotspotPositions() {
      const slides = this.querySelectorAll('.rk-stl__product-slide');
      this.hotspots = this.querySelectorAll('.rk-stl__hotspot');

      slides.forEach((slide, i) => {
        const hotspot = this.hotspots[i];
        if (!hotspot) return;

        const x = slide.dataset.hotspotX;
        const y = slide.dataset.hotspotY;

        if (x != null) hotspot.style.left = x + '%';
        if (y != null) hotspot.style.top = y + '%';
      });
    }

    /* ------------------------------------------------------------------
       Carousel Init — capture Swiper instance
       ------------------------------------------------------------------ */
    _onCarouselInit(e) {
      this.swiper = e.detail.swiper;

      // Bind slideChange for bidirectional sync
      this._onSlideChange = () => {
        const activeIndex = this.swiper.realIndex;
        this._activateHotspot(activeIndex);
      };

      this.swiper.on('slideChange', this._onSlideChange);
    }

    /* ------------------------------------------------------------------
       Hotspot click → navigate carousel
       ------------------------------------------------------------------ */
    _onHotspotClick(e) {
  const hotspot = e.target.closest('.rk-stl__hotspot');
  if (!hotspot) return;

  const index = parseInt(hotspot.dataset.hotspotIndex, 10);
  if (isNaN(index)) return;

  this._activateHotspot(index);

  if (this.swiper) {
    this.swiper.slideTo(index);
  } else {
    const slides = this.querySelectorAll('.rk-stl__product-slide');
    const target = slides[index];
    if (!target) return;

    slides.forEach(s => s.classList.remove('is-highlighted'));
    target.classList.add('is-highlighted');
    target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    setTimeout(() => target.classList.remove('is-highlighted'), 1800);
  }
}

    /* ------------------------------------------------------------------
       Mobile/touch: tocar no card abre o quick-add (escolha de tamanho)
       em vez de navegar para a página do produto — mantém a cliente na
       home e encurta o caminho de compra do look completo.
       Desktop segue navegando normalmente pelo link do card.
       ------------------------------------------------------------------ */
    _onProductLinkTap(e) {
      const isMobile = window.matchMedia('(max-width: 749px)').matches
        || window.matchMedia('(pointer: coarse)').matches;

      // Qualquer interação de quick-add dentro do STL (botão sacolinha ou
      // card) marca a próxima adição como "vinda do look" — usado para não
      // abrir a sacola e manter o scroll (_onCartAddFromStl).
      if (isMobile && e.target.closest('.rk-stl-quick-add')) {
        this._markStlQuickAdd();
      }

      const link = e.target.closest('.rk-stl__product-link');
      if (!link) return;
      if (!isMobile) return;

      const slide = link.closest('.rk-stl__product-slide');
      const quickAdd = slide && slide.querySelector('quick-add-component');
      if (!quickAdd || typeof quickAdd.handleClick !== 'function') return;

      e.preventDefault();
      e.stopPropagation();
      this._markStlQuickAdd();
      quickAdd.handleClick(new MouseEvent('click', { bubbles: true, cancelable: true }));
    }

    /* Marca a intenção e agenda a limpeza: se o modal fechar sem adição,
       a flag morre junto (não pode vazar para adições de outras seções). */
    _markStlQuickAdd() {
      this._stlQuickAddPending = true;
      setTimeout(() => {
        const dlg = document.querySelector('#quick-add-dialog dialog');
        if (dlg) {
          dlg.addEventListener('close', () => { this._stlQuickAddPending = false; }, { once: true });
        }
      }, 300);
    }

    /* Adição ao carrinho vinda do STL (mobile): segura o auto-open da
       sacola e devolve o scroll pro look — a cliente segue exatamente de
       onde parou, a um toque da segunda peça. A sacolinha do header
       continua atualizando o contador como feedback. */
    _onCartAddFromStl(e) {
      if (!this._stlQuickAddPending) return;

      const isMobile = window.matchMedia('(max-width: 749px)').matches
        || window.matchMedia('(pointer: coarse)').matches;
      this._stlQuickAddPending = false;
      if (!isMobile) return;

      // e.detail.data.didError etc. — só age em adição bem-sucedida
      if (e.detail && e.detail.data && e.detail.data.didError) return;

      const drawer = document.querySelector('cart-drawer-component[auto-open]');
      if (drawer) {
        drawer.removeAttribute('auto-open');
        setTimeout(() => drawer.setAttribute('auto-open', ''), 1500);
      }

      const scrollY = window.scrollY;
      // pequena pausa para o botão mostrar o estado "adicionado" antes de fechar
      setTimeout(() => {
        const dlgComp = document.getElementById('quick-add-dialog');
        const dlg = dlgComp && dlgComp.querySelector('dialog');
        if (dlg && dlg.open && typeof dlgComp.closeDialog === 'function') {
          dlgComp.closeDialog();
        }
        // o scroll-lock do dialog pode devolver a página no topo — reancora
        requestAnimationFrame(() => window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' }));
        setTimeout(() => window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' }), 200);
      }, 650);
    }

    /* ------------------------------------------------------------------
       Activate hotspot by index
       ------------------------------------------------------------------ */
    _activateHotspot(index) {
      this.hotspots.forEach((h, i) => {
        h.classList.toggle('is-active', i === index);
      });
    }
  }

  customElements.define('rk-shop-the-look', RkShopTheLook);
}
